import { useMemo } from "react";
import { type AuditSession } from "../../models/audit/auditSession";
import { getColumnDisplayName } from "../../lib/columnNameUtils";
import {
  analyticsResultIdFromSelection,
  analyticsSummaryToCheckResult,
  analyticsVerdictText,
} from "../../models/audit/analyticsTranslator";
import { useAnalyticsResultDetail } from "./useAnalyticsResultDetail";

interface UseAnalyticsResultDataProps {
  auditSession: AuditSession;
  checkResultId?: string;
}

/**
 * The detail-panel data for an analytics cell, resolved from its selection id.
 *
 * Sibling of `useAttributeTestData`, and thinner for a reason rather than by
 * omission: an analytics cell's selection id carries the verdict id outright, so
 * there is no legacy id shape to resolve through the table shell and no source
 * row to load before the detail fetch can fire.
 */
export function useAnalyticsResultData({
  auditSession,
  checkResultId,
}: UseAnalyticsResultDataProps) {
  const analyticsResultId = analyticsResultIdFromSelection(checkResultId);

  const {
    data: analyticsResult,
    isLoading,
    error,
    refetch,
  } = useAnalyticsResultDetail(auditSession.id, analyticsResultId);

  const column = useMemo(() => {
    if (!analyticsResult) return undefined;
    return auditSession.procedure.columns.find(
      (c) => c.analyticsColumn?.id === analyticsResult.analyticsColumnId,
    );
  }, [analyticsResult, auditSession.procedure.columns]);

  const columnName = useMemo(() => {
    // The empty control-attribute list is safe: `getColumnDisplayName` reads it
    // only to build an attribute-reference ordinal, and that branch is gated on
    // `column.attributeTestColumn`, which an analytics column does not have. An
    // analytics column names itself from its own name or bound workflow, so
    // hydrating the control would buy nothing.
    return column
      ? getColumnDisplayName(column, auditSession.procedure.columns, [])
      : "";
  }, [column, auditSession.procedure.columns]);

  const checkResult = useMemo(() => {
    if (!analyticsResult || !column) return undefined;
    return {
      ...analyticsSummaryToCheckResult(analyticsResult, column),
      // The badge subset carries no prose, so the translator leaves `reason`
      // empty for the grid. This is the call site that has the conclusion.
      reason: analyticsVerdictText(analyticsResult) ?? "",
    };
  }, [analyticsResult, column]);

  return {
    analyticsResultId,
    analyticsResult,
    isLoading,
    // Surfaced so the panel can render the fetch's own failure — a 403, a 500,
    // a network drop or a schema violation — instead of the "not found" copy,
    // and `refetch` (an `invalidate` under the hood) drives a retry. A retry
    // recovers a transient failure; a schema violation re-parses the same
    // payload and re-fails, so the panel answers that kind with a page refresh
    // rather than a retry.
    error,
    refetch,
    column,
    columnName,
    checkResult,
  };
}
